import { useCallback, useEffect, useState } from "react";
import { config } from "../../config";

interface AuditLog {
  id: string;
  timestamp: string;
  action: string;
  actorId: string;
  actorEmail: string;
  targetType: string;
  targetId: string;
  changes?: Record<string, { old: unknown; new: unknown }>;
  metadata?: Record<string, unknown>;
}

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  // Filters
  const today = new Date().toISOString().substring(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [targetType, setTargetType] = useState("");
  const [actorId, setActorId] = useState("");

  const fetchLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set("startDate", startDate);
      params.set("endDate", endDate);
      if (targetType) params.set("targetType", targetType);
      if (actorId) params.set("actorId", actorId);
      params.set("limit", "200");

      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/audit-logs?${params.toString()}`,
        { credentials: "include" },
      );

      if (!response.ok) throw new Error("Failed to fetch audit logs");

      const data = await response.json();
      setLogs(data.logs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, targetType, actorId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpanded = (id: string) => {
    setExpandedLog(expandedLog === id ? null : id);
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Audit Logs</h1>
      </header>

      <div className="admin-toolbar audit-filters">
        <div className="filter-group">
          <label htmlFor="startDate">From</label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="endDate">To</label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="targetType">Target Type</label>
          <select
            id="targetType"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          >
            <option value="">All</option>
            <option value="organization">Organization</option>
            <option value="user">User</option>
            <option value="domain">Domain</option>
            <option value="impersonation">Impersonation</option>
            <option value="settings">Settings</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="actorId">Actor ID</label>
          <input
            id="actorId"
            type="text"
            placeholder="Filter by actor..."
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
          />
        </div>
      </div>

      {isLoading && (
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Loading audit logs...</p>
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button type="button" onClick={fetchLogs}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && logs.length === 0 && (
        <div className="admin-empty">
          <p>No audit logs found for the selected filters.</p>
        </div>
      )}

      {!isLoading && logs.length > 0 && (
        <>
          <p className="text-muted">{logs.length} log entries</p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Target Type</th>
                <th>Target ID</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <>
                  <tr key={log.id}>
                    <td>{new Date(log.timestamp).toLocaleString()}</td>
                    <td>
                      <span className="badge">{log.action}</span>
                    </td>
                    <td title={log.actorId}>{log.actorEmail}</td>
                    <td>{log.targetType}</td>
                    <td className="text-mono">{log.targetId}</td>
                    <td>
                      {(log.changes || log.metadata) && (
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => toggleExpanded(log.id)}
                        >
                          {expandedLog === log.id ? "Hide" : "Show"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedLog === log.id && (
                    <tr key={`${log.id}-details`} className="expanded-row">
                      <td colSpan={6}>
                        <div className="audit-details">
                          {log.changes && (
                            <div>
                              <strong>Changes:</strong>
                              <pre>{JSON.stringify(log.changes, null, 2)}</pre>
                            </div>
                          )}
                          {log.metadata && (
                            <div>
                              <strong>Metadata:</strong>
                              <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
